import { Product } from "@omni/types/api/product/bff/product.model";
import { ProductFieldSet } from "@omni/types/api/product/bff/shared/enums/productFieldSet.enum";
import { RequireChildrenFor } from "@omni/types/components/utils/children.types";
import { isDefined } from "@omni/utils/helper.utils";
import { createContext, use, useMemo } from "react";

import { Mapped } from "@/context/mapped.context";

type ProductsContextValue = {
    products: Array<Product<ProductFieldSet.Full>>;
};

type ProductsContextProviderProps = RequireChildrenFor<{
    products?: Array<Product<ProductFieldSet.Full>>;
    urlState?: string;
}>;

export const ProductsContext = createContext<ProductsContextValue | undefined>(
    undefined
);
ProductsContext.displayName = "ProductsContext";

export const ProductsContextProvider = ({
    products = [],
    urlState,
    children
}: ProductsContextProviderProps) => {
    const enhancedProducts = useMemo(
        () =>
            products.map((product) => ({
                ...product,
                url: `${product.url}${urlState ? `?pc=${urlState}` : ""}`
            })),
        [products, urlState]
    );

    return (
        <ProductsContext value={{ products: enhancedProducts }}>
            <Mapped array={enhancedProducts}>{children}</Mapped>
        </ProductsContext>
    );
};

export const useProductsContext = () => {
    const context = use<ProductsContextValue | undefined>(ProductsContext);

    if (!isDefined(context)) {
        throw new Error(`${ProductsContext.displayName} is undefined.`);
    }

    return context;
};
